import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { DependencyFilterKeys } from '../../../../../../shared/dependenciesV2';
import { allFetchersMap } from '../../../../../../shared/fetchers';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import { selectOptionFactory } from '../../../../../utils';
import FilterCardMultiSelect from '../../../../generic/Filter/FilterCardMultiSelect';
import useFetchDependenciesAction from '../../hooks/useFetchDependencyAction';
import usePackageManagers from '../../hooks/usePackageManagers';
import type { SelectOption } from '../../../../../types';

/**
 * Package manager filter which allows the user to filter dependencies by direct or transitive.
 * Uses the DepsV2 package-managers endpoint to obtain a list of available package managers.
 */
const PackageManagerFilter: VoidFunctionComponent = () => {
  const filters = useSelector(DependenciesV2Store.selectors.getFilters);
  const setFilter = useFetchDependenciesAction(DependenciesV2Store.actions.setFilters);

  const { arePackageManagersLoading, packageManagers } = usePackageManagers();
  const isDisabled = arePackageManagersLoading || packageManagers.length <= 1;
  const hasSinglePackageManager = packageManagers.length === 1;

  const selectedPackageManagers = (filters.fetchers ?? []).map(
    (fetcher) => selectOptionFactory(fetcher, allFetchersMap[fetcher] ?? fetcher),
  );
  const onChange = (selected: SelectOption[]) => setFilter({
    ...filters,
    [DependencyFilterKeys.Fetchers]: selected.map((option) => option.value),
  });

  return (
    <FilterCardMultiSelect
      label="Package Manager"
      placeholder={arePackageManagersLoading ? 'Loading...' : 'Select one or more...'}
      options={packageManagers}
      values={hasSinglePackageManager ? packageManagers : selectedPackageManagers}
      onChange={onChange}
      isDisabled={isDisabled}
    />
  );
};

export default PackageManagerFilter;
